/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : store/backupErrorStore
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : Which error the detail Drawer is showing, held app-wide (mirrors
 *                  backupActivityStore) so an ALERT can open it from anywhere — the bell, the
 *                  /alerts page, or the Errors screen itself. Without a global mount, an alert
 *                  deep-link would be a dead end (FR-008).
 *
 *                  NOT persisted: a refresh should not silently reopen an error drawer.
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import { create } from 'zustand';

interface ErrorDrawerState {
  /** The error currently shown in the drawer, or null when closed. */
  errorKey: string | null;
  /**
   * The specific run to display, when the caller knows it (a clicked row does; an alert
   * deep-link does not). Null shows the LATEST occurrence of the problem.
   * 🛑 Without this the drawer resolved the problem (surface+entity+code) and rendered the newest
   * occurrence — so clicking a failure from three days ago showed a different run's error text.
   */
  runId: string | null;
  open: (errorKey: string, runId?: string | null) => void;
  close: () => void;
}

export const useBackupErrorDrawer = create<ErrorDrawerState>((set) => ({
  errorKey: null,
  runId: null,
  open: (errorKey, runId = null) => set({ errorKey, runId }),
  close: () => set({ errorKey: null, runId: null }),
}));

export default useBackupErrorDrawer;
